import { useState } from 'react';
import { useSeonSessionMutation } from './webapi/client/useSeonSessionMutation';

interface SeonConfig {
  host: string;
  session_id: string;
  audio_fingerprint: boolean;
  canvas_fingerprint: boolean;
  webgl_fingerprint: boolean;
  max_proxy_delay: number;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface SeonInstance {
  config: (body: SeonConfig) => void;
  getBase64Session: () => Promise<string>;
}

declare global {
  interface Window {
    seon: SeonInstance;
  }
}

const SEON_SCRIPT_ID = 'seon-agent';

export const useSeon = () => {
  const [sessionId, setSessionId] = useState<string>('');
  const { mutateAsync: sendSeonSession } = useSeonSessionMutation();

  const createSeonScript = async () => {
    return new Promise((resolve, reject) => {
      // To prevent attachment of the script twice
      if (document.getElementById(SEON_SCRIPT_ID)) {
        resolve('');
        return;
      }

      const seonScript = document.createElement('script');
      seonScript.src = 'https://cdn.seondf.com/js/v5/agent.js';
      seonScript.async = true;
      seonScript.defer = true;
      seonScript.id = SEON_SCRIPT_ID;

      seonScript.onload = () => resolve('');
      seonScript.onerror = () =>
        reject(new Error('Failed to load SEON script'));

      document.head.appendChild(seonScript);
    });
  };

  const getSessionId = async () => {
    if (sessionId) return sessionId;

    if (!window.seon) {
      await createSeonScript();
    }

    const sessionIdBase64 = await window.seon.getBase64Session();
    setSessionId(sessionIdBase64);
    return sessionIdBase64;
  };

  const sendSeonFraudInfo = async () => {
    const sessionIdFetched = await getSessionId();

    window.seon.config({
      host: 'seondf.com',
      session_id: sessionIdFetched,
      audio_fingerprint: true,
      canvas_fingerprint: true,
      webgl_fingerprint: true,
      max_proxy_delay: 1000,
      onSuccess: () => undefined,
      onError: () => undefined,
    });

    await sendSeonSession({
      session: sessionId,
    });
  };

  return {
    sendSeonFraudInfo,
    createSeonScript,
  };
};
